import { makeStyles } from '@material-ui/core/styles'
import {
  CALLOUT_BACKGROUND,
  CALLOUT_FOREGROUND,
  getOutlinedTextFieldWrapperStyles,
} from 'lib/form-field-styles'
import { SEGMENTED_BUTTON_BORDER } from 'lib/segmented-button-styles'

const noteSurfaceCard = theme => ({
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  overflow: 'visible',
  marginBottom: theme.spacing(1.5),
  borderRadius: 12,
  border: '1px solid #E0E0E0',
  boxShadow: 'none',
})

const noteSurfaceContent = theme => ({
  padding: theme.spacing(3),
  '&:last-child': {
    paddingBottom: theme.spacing(3),
  },
})

export const useNoteComposerStyles = makeStyles(theme => ({
  card: {
    ...noteSurfaceCard(theme),
    backgroundColor: theme.palette.common.white,
  },
  cardContent: {
    ...noteSurfaceContent(theme),
  },
  form: {
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  embedded: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
  },
  heading: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
    textTransform: 'uppercase',
  },
  formField: {
    ...getOutlinedTextFieldWrapperStyles(theme),
    marginBottom: theme.spacing(1.5),
    '& .MuiInputBase-root': {
      ...getOutlinedTextFieldWrapperStyles(theme)['& .MuiInputBase-root'],
      minHeight: 40,
    },
    '& .MuiInputBase-inputMultiline': {
      padding: 0,
    },
  },
  contentField: {
    marginBottom: 0,
  },
  privacySection: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  privacyGroup: {
    display: 'inline-flex !important',
    flexWrap: 'nowrap',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    width: 'max-content !important',
    maxWidth: 'none !important',
    minWidth: 0,
    flex: '0 0 auto !important',
    overflow: 'visible !important',
    boxSizing: 'border-box',
    backgroundColor: `${theme.palette.common.white} !important`,
    border: `1px solid ${SEGMENTED_BUTTON_BORDER} !important`,
    '& .MuiToggleButtonGroup-groupedHorizontal': {
      borderRadius: '0 !important',
    },
    '& .MuiToggleButton-root': {
      borderTop: 'none !important',
      borderRight: 'none !important',
      borderBottom: 'none !important',
    },
  },
  privacyButton: {
    display: 'inline-flex !important',
    alignItems: 'center !important',
    justifyContent: 'center !important',
    flex: '0 0 auto !important',
    flexGrow: '0 !important',
    flexShrink: '0 !important',
    flexBasis: 'auto !important',
    width: 'auto !important',
    minWidth: 'max-content !important',
    maxWidth: 'none !important',
    overflow: 'visible !important',
    boxSizing: 'border-box',
    padding: '10px 16px !important',
    lineHeight: 1,
    border: 'none !important',
    color: `${CALLOUT_FOREGROUND} !important`,
    backgroundColor: `${theme.palette.common.white} !important`,
    '&:not(:first-of-type)': {
      borderLeft: `1px solid ${SEGMENTED_BUTTON_BORDER} !important`,
    },
    '&:hover': {
      backgroundColor: `${theme.palette.common.white} !important`,
    },
    '&.Mui-selected, &.MuiToggleButton-selected': {
      backgroundColor: `${CALLOUT_BACKGROUND} !important`,
      color: `${CALLOUT_FOREGROUND} !important`,
      border: 'none !important',
      '&:hover': {
        backgroundColor: `${CALLOUT_BACKGROUND} !important`,
      },
    },
    '&.Mui-selected + &, &.MuiToggleButton-selected + &': {
      borderLeft: `1px solid ${SEGMENTED_BUTTON_BORDER} !important`,
    },
    '& .MuiToggleButton-label': {
      display: 'flex !important',
      alignItems: 'center !important',
      justifyContent: 'center !important',
      width: '100% !important',
      height: '100%',
      margin: 0,
      overflow: 'visible !important',
      textOverflow: 'clip',
      whiteSpace: 'nowrap',
    },
    '& .MuiToggleButton-label > span': {
      display: 'inline-flex !important',
      alignItems: 'center !important',
      justifyContent: 'center !important',
      width: 'auto !important',
      maxWidth: 'none !important',
      overflow: 'visible !important',
      flexShrink: '0 !important',
      whiteSpace: 'nowrap',
    },
  },
  privacyLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    gap: theme.spacing(0.75),
    lineHeight: 1,
    whiteSpace: 'nowrap',
    overflow: 'visible',
    '& .MuiSvgIcon-root': {
      fontSize: 16,
      flexShrink: 0,
      color: 'currentColor',
    },
  },
  helper: {
    margin: theme.spacing(1, 0, 0),
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: 0,
  },
  saveButton: {
    borderRadius: 24,
    padding: theme.spacing(0.75, 2.5),
    fontWeight: 600,
  },
}))

export const useNoteCardStyles = makeStyles(theme => ({
  card: {
    ...noteSurfaceCard(theme),
    '&:hover': {
      backgroundColor: '#FAFBFC',
      borderColor: '#D0D5D8',
    },
  },
  cardContent: {
    ...noteSurfaceContent(theme),
  },
  titleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  activateArea: {
    flex: 1,
    minWidth: 0,
    marginRight: theme.spacing(1),
    cursor: 'pointer',
    '&:focus': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  title: {
    fontWeight: 600,
    fontSize: '0.9375rem',
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginBottom: theme.spacing(1),
  },
  contentPreview: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
    gap: theme.spacing(1),
  },
  date: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  chipRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  menuWrap: {
    flexShrink: 0,
  },
  menuButton: {
    cursor: 'pointer',
    minWidth: 0,
    padding: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  menuIcon: {
    minWidth: 36,
    color: 'rgba(0, 0, 0, 0.54)',
  },
}))

export const useStudentNotesStyles = makeStyles(theme => ({
  notesContainer: {
    paddingTop: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(3),
    },
  },
  notesColumn: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  notesList: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  },
  notesListTitle: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    color: '#172024',
  },
}))
